<template>
  <aside
    class="viq-sidebar"
    :class="{
      'viq-sidebar--collapsed': collapsed,
      'viq-sidebar--open': !collapsed,
    }"
  >
    <template v-if="!collapsed">
      <div class="viq-sidebar__header">
        <button class="viq-sidebar__new-btn" @click="newConversation">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          New Chat
        </button>
        <button class="viq-sidebar__collapse-btn" title="Collapse sidebar" @click="toggleSidebar">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M11 4L6 9l5 5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M7 4L2 9l5 5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="viq-sidebar__sections">
        <!-- Lenses section (feature-flagged) -->
        <div v-if="lensesEnabled" class="viq-sidebar__section">
          <div class="viq-sidebar__section-header" @click="lensesExpanded = !lensesExpanded">
            <span class="viq-sidebar__section-left">
              <svg class="viq-sidebar__section-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <ellipse cx="7" cy="7" rx="6" ry="3" stroke="currentColor" stroke-width="1.2" />
                <circle cx="7" cy="7" r="1.5" fill="currentColor" />
              </svg>
              <span class="viq-sidebar__section-title">LENSES</span>
            </span>
            <span class="viq-sidebar__section-actions">
              <el-tooltip content="Lenses let you focus VIQ on specific regulatory areas" placement="top" :open-delay="400">
                <span class="viq-sidebar__help-badge">?</span>
              </el-tooltip>
              <svg
                class="viq-sidebar__chevron"
                :class="{ 'viq-sidebar__chevron--expanded': lensesExpanded }"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path d="M4 5l2 2 2-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
          <div v-if="lensesExpanded" class="viq-sidebar__section-content">
            <ViqLensSelector />
            <ViqLensBuilder v-if="mode === 'lens-build'" />
          </div>
        </div>

        <div class="viq-sidebar__divider" />

        <!-- Interactions section -->
        <div class="viq-sidebar__section">
          <div class="viq-sidebar__section-header" @click="interactionsExpanded = !interactionsExpanded">
            <span class="viq-sidebar__section-left">
              <svg class="viq-sidebar__section-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M12 9a1.5 1.5 0 01-1.5 1.5H4.5L2 13V3.5A1.5 1.5 0 013.5 2h7A1.5 1.5 0 0112 3.5V9z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="viq-sidebar__section-title">INTERACTIONS</span>
            </span>
            <span class="viq-sidebar__section-actions">
              <el-tooltip content="Your conversation history" placement="top" :open-delay="400">
                <span class="viq-sidebar__help-badge">?</span>
              </el-tooltip>
              <span v-if="conversationCount > 0" class="viq-sidebar__count-badge">{{ conversationCount }}</span>
              <svg
                class="viq-sidebar__chevron"
                :class="{ 'viq-sidebar__chevron--expanded': interactionsExpanded }"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path d="M4 5l2 2 2-2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
          <div v-if="interactionsExpanded" class="viq-sidebar__section-content">
            <ViqConversationList />
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="viq-sidebar__collapsed-strip">
        <button class="viq-sidebar__expand-btn" title="Expand sidebar" @click="toggleSidebar">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M7 4l5 5-5 5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M11 4l5 5-5 5" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <button class="viq-sidebar__collapsed-new" title="New Chat" @click="newConversation">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          </svg>
        </button>
        <div class="viq-sidebar__collapsed-avatars">
          <span
            v-for="conv in collapsedAvatars"
            :key="conv.id"
            class="viq-sidebar__collapsed-avatar"
            :class="{ 'viq-sidebar__collapsed-avatar--active': activeConversation && activeConversation.id === conv.id }"
            :title="conv.title"
            @click="selectConversation(conv)"
          >
            {{ conv.title ? conv.title.charAt(0).toLowerCase() : '?' }}
          </span>
        </div>
      </div>
    </template>
  </aside>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  name: "ViqSidebar",
  components: {
    ViqConversationList: () => import("./ViqConversationList"),
    ViqLensSelector: () => import("./ViqLensSelector"),
    ViqLensBuilder: () => import("./ViqLensBuilder"),
  },
  data() {
    return {
      lensesExpanded: false,
      interactionsExpanded: true,
    };
  },
  computed: {
    ...mapState("Viq", {
      collapsed: "sidebarCollapsed",
      conversations: "conversations",
      activeConversation: "activeConversation",
      mode: "mode",
    }),
    ...mapGetters("Viq", ["hasConversations"]),
    lensesEnabled() {
      return !!(this.$config.features && this.$config.features.viqLenses);
    },
    conversationCount() {
      return this.conversations ? this.conversations.length : 0;
    },
    collapsedAvatars() {
      if (!this.conversations || this.conversations.length === 0) return [];
      return [...this.conversations]
        .sort((a, b) => {
          const dateA = new Date(a.updated_at || a.created_at);
          const dateB = new Date(b.updated_at || b.created_at);
          return dateB - dateA;
        })
        .slice(0, 8);
    },
  },
  watch: {
    mode(val) {
      if (val === "lens-build") {
        this.lensesExpanded = true;
      }
    },
  },
  methods: {
    toggleSidebar() {
      this.$store.dispatch("Viq/toggleSidebar");
    },
    newConversation() {
      this.$store.dispatch("Viq/newConversation");
    },
    selectConversation(conv) {
      this.$store.dispatch("Viq/loadConversation", conv.id);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-sidebar {
  position: relative;
  z-index: 10;
  background: $viq-bg-sidebar;
  border-radius: $viq-radius-lg;
  box-shadow: $viq-shadow-sidebar;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  transition: width $viq-transition-base, min-width $viq-transition-base;
  overflow: hidden;

  &--open {
    width: $viq-sidebar-width;
    min-width: $viq-sidebar-width;
  }

  &--collapsed {
    width: $viq-sidebar-collapsed-width;
    min-width: $viq-sidebar-collapsed-width;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-md;
  }

  &__new-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $viq-space-sm;
    flex: 1;
    padding: 10px $viq-space-md;
    background: transparent;
    border: 1px solid $viq-accent;
    border-radius: $viq-radius-md;
    color: $viq-accent;
    font-size: $viq-font-sm;
    font-weight: 600;
    cursor: pointer;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-accent-light;
    }
  }

  &__collapse-btn,
  &__expand-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: 1px solid $viq-border;
    background: transparent;
    color: $viq-text-muted;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    line-height: 1;
    padding-bottom: 3px;
    transition: background $viq-transition-fast, color $viq-transition-fast;

    &:hover {
      background: $viq-bg-secondary;
      color: $viq-text-primary;
    }
  }

  &__sections {
    flex: 1;
    overflow-y: auto;
    padding: 0 $viq-space-sm $viq-space-md;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
  }

  &__section {
    margin-bottom: $viq-space-xs;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: $viq-space-sm;
    cursor: pointer;
    user-select: none;
    border-radius: $viq-radius-sm;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-bg-secondary;
    }
  }

  &__section-left {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__section-icon {
    color: $viq-text-muted;
    flex-shrink: 0;
  }

  &__section-title {
    font-size: $viq-font-xs;
    font-weight: 600;
    color: $viq-text-muted;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__section-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__help-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid $viq-border;
    font-size: 10px;
    font-weight: 600;
    color: $viq-text-muted;
    cursor: help;
  }

  &__count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: $viq-radius-full;
    background: $viq-bg-secondary;
    font-size: 10px;
    font-weight: 600;
    color: $viq-text-secondary;
  }

  &__chevron {
    color: $viq-text-muted;
    transition: transform $viq-transition-fast;
    transform: rotate(-90deg);

    &--expanded {
      transform: rotate(0deg);
    }
  }

  &__section-content {
    padding-top: $viq-space-xs;
  }

  &__divider {
    height: 1px;
    background: $viq-border;
    margin: $viq-space-sm $viq-space-sm;
  }

  // Collapsed strip
  &__collapsed-strip {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: $viq-space-sm 0;
    gap: $viq-space-sm;
  }

  &__collapsed-new {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid $viq-border;
    background: transparent;
    color: $viq-accent;
    cursor: pointer;
    border-radius: $viq-radius-sm;
    transition: background $viq-transition-fast;

    &:hover {
      background: $viq-accent-light;
    }
  }

  &__collapsed-avatars {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $viq-space-xs;
    padding-top: $viq-space-xs;
  }

  &__collapsed-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: $viq-bg-secondary;
    color: $viq-text-secondary;
    font-size: $viq-font-xs;
    font-weight: 600;
    cursor: pointer;
    transition: background $viq-transition-fast, color $viq-transition-fast;
    text-transform: lowercase;

    &:hover {
      background: $viq-accent-light;
      color: $viq-accent;
    }

    &--active {
      background: $viq-accent-light;
      color: $viq-accent;
    }
  }
}
</style>
